export type AnswerValue = 'YES' | 'NO' | 'NA';

export interface NCInfo {
  id: string;
  object_id: string;
  level: string;
  status: string;
  description: string;
  rectification_required: string;
  category_id: string;
  contractor_id: string | null;
  location: string;
}

export interface FlagInfo {
  status: 'OPEN' | 'ADDRESSED';
  note: string;
  reply: string;
  by: string;
  at: string;
}

export interface Evidence {
  id: string;
  url: string;
  type: 'PHOTO' | 'DOCUMENT';
  caption: string;
  uploaded_at: string;
  by: string;
}

export interface Question {
  id: string;
  code: string;
  kind: 'PRIMARY' | 'SECONDARY';
  text: string;
  priority: string | null;
  answer: AnswerValue | null;
  remarks: string;
  nc: NCInfo | null;
  flag: FlagInfo | null;
  starred: boolean;
  evidence: Evidence[];
  secondaries?: Question[];
}

export interface ClosedInfo {
  reason: string;
  reason_label: string;
  remarks: string;
}

export interface Area {
  id: string;
  code: string;
  kind: 'DOMAIN' | 'TRADE' | 'SUB_DOMAIN';
  name: string;
  closed: ClosedInfo | null;
  children: (Area | Question)[];
}

export const isQuestion = (node: Area | Question): node is Question =>
  node.kind === 'PRIMARY' || node.kind === 'SECONDARY';

/** Every question (primaries and their secondaries) under an area, in order. */
export function questionsUnder(area: Area): Question[] {
  const out: Question[] = [];
  for (const child of area.children) {
    if (isQuestion(child)) {
      out.push(child);
      out.push(...(child.secondaries || []));
    } else {
      out.push(...questionsUnder(child));
    }
  }
  return out;
}

/** Answered / total questions, leaving out anything inside a closed area. */
export function progressOf(area: Area): { answered: number; total: number } {
  if (area.closed) return { answered: 0, total: 0 };
  let answered = 0;
  let total = 0;
  for (const child of area.children) {
    if (isQuestion(child)) {
      const all = [child, ...(child.secondaries || [])];
      total += all.length;
      answered += all.filter((q) => q.answer).length;
    } else {
      const sub = progressOf(child);
      answered += sub.answered;
      total += sub.total;
    }
  }
  return { answered, total };
}

export function findArea(areas: Area[], id: string): { area: Area; closedAbove: Area | null } | null {
  const walk = (list: Area[], closedAbove: Area | null): { area: Area; closedAbove: Area | null } | null => {
    for (const area of list) {
      const above = closedAbove || (area.closed ? area : null);
      if (area.id === id) return { area, closedAbove: closedAbove };
      const inner = walk(area.children.filter((c): c is Area => !isQuestion(c)), above);
      if (inner) return inner;
    }
    return null;
  };
  return walk(areas, null);
}

/** A copy of the tree with one question changed. */
export function patchQuestion(areas: Area[], id: string, patch: Partial<Question>): Area[] {
  const fix = (q: Question): Question => {
    const next = q.id === id ? { ...q, ...patch } : q;
    return next.secondaries ? { ...next, secondaries: next.secondaries.map(fix) } : next;
  };
  const walk = (area: Area): Area => ({
    ...area,
    children: area.children.map((c) => (isQuestion(c) ? fix(c) : walk(c))),
  });
  return areas.map(walk);
}

/** Questions the reviewer has flagged (open or addressed) under an area. */
export const flaggedUnder = (area: Area): Question[] => questionsUnder(area).filter((q) => q.flag);

/** Every question under an area that is not inside a closed area. */
export function activeQuestions(area: Area): Question[] {
  if (area.closed) return [];
  const out: Question[] = [];
  for (const child of area.children) {
    if (isQuestion(child)) {
      out.push(child);
      out.push(...(child.secondaries || []));
    } else {
      out.push(...activeQuestions(child));
    }
  }
  return out;
}

/** A point the auditors still have to finish: unanswered, or a No without NC or evidence. */
export const needsAttention = (q: Question): boolean =>
  !q.answer || (q.answer === 'NO' && (!q.nc || q.evidence.length === 0));

export interface PointEntry {
  q: Question;
  path: string;
  parent: Question | null;
}

/** Points that pass `keep`, each with where it sits (area names) and its primary if it is a secondary. */
export function collectPoints(areas: Area[], keep: (q: Question) => boolean): PointEntry[] {
  const out: PointEntry[] = [];
  const walk = (area: Area, trail: string[]) => {
    if (area.closed) return;
    const here = [...trail, `${area.code} ${area.name}`];
    for (const child of area.children) {
      if (isQuestion(child)) {
        if (keep(child)) out.push({ q: child, path: here.join(' › '), parent: null });
        for (const sec of child.secondaries || []) {
          if (keep(sec)) out.push({ q: sec, path: here.join(' › '), parent: child });
        }
      } else {
        walk(child, here);
      }
    }
  };
  areas.forEach((a) => walk(a, []));
  return out;
}
